import { supabase } from '../lib/supabase'
import { Client, CreateClientData } from '../types'

export const clientService = {
  // Get all clients for the current user
  async getClientsForUser(userId: string): Promise<Client[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true })
    
    if (error) throw error
    return data || []
  },

  // Get a specific client by ID
  async getClientById(clientId: string): Promise<Client | null> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single()
    
    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw error
    }
    return data
  },

  // Create a new client
  async createClient(clientData: CreateClientData, userId: string): Promise<Client> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('clients')
      .insert({
        ...clientData,
        user_id: userId
      })
      .select('*')
      .single()
    
    if (error) throw error
    return data
  },

  // Update a client
  async updateClient(clientId: string, updates: Partial<CreateClientData>): Promise<Client> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('clients')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', clientId)
      .select('*')
      .single()
    
    if (error) throw error
    return data
  },

  // Delete a client
  async deleteClient(clientId: string): Promise<void> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId)
    
    if (error) throw error
  },

  // Search clients by name or email
  async searchClients(userId: string, query: string): Promise<Client[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', userId)
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .order('name', { ascending: true })
    
    if (error) throw error
    return data || []
  }
} 